import * as Sentry from "@sentry/react";
import { browserSentryOptions } from "@mediatechgroup/observability";
import { makeBeforeSend } from "./sentryBeforeSend";
import { AxiosError } from "axios";
import apiConfig from "../api/config";

// Error severity levels for categorization
export type ErrorSeverity = "fatal" | "error" | "warning" | "info";

// Context tags for categorizing errors
export interface SentryContext {
  component?: string;
  operation?: string;
  userId?: string;
  tenantId?: string;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Convert an unknown error to an Error object with a meaningful message
 */
const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  // Handle Supabase/Postgrest errors and similar objects with message property
  if (error && typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;

    // Extract message from common error object patterns
    if (typeof errorObj.message === 'string') {
      const errorMessage = errorObj.code
        ? `${errorObj.code}: ${errorObj.message}`
        : errorObj.message;
      return new Error(errorMessage);
    }

    // Handle errors with only a code
    if (typeof errorObj.code === 'string') {
      return new Error(`Error code: ${errorObj.code}`);
    }

    // Try to stringify the object if it has useful properties
    try {
      const stringified = JSON.stringify(error);
      if (stringified !== '{}') {
        return new Error(stringified);
      }
    } catch {
      // JSON.stringify failed, fall through to default
    }
  }

  // Fallback for primitives and other types
  return new Error(String(error));
};

/**
 * Capture an exception with standardized context
 */
export const captureError = (
  error: Error | unknown,
  context?: SentryContext,
  severity: ErrorSeverity = "error"
): void => {
  const errorObj = toError(error);

  Sentry.withScope((scope) => {
    scope.setLevel(severity);

    if (context) {
      // Set tags for filtering in Sentry dashboard
      if (context.component) scope.setTag("component", context.component);
      if (context.operation) scope.setTag("operation", context.operation);
      if (context.userId) scope.setTag("user_id", context.userId);
      if (context.tenantId) scope.setTag("tenant_id", context.tenantId);

      // Set extra context data
      scope.setExtras(context);
    }

    Sentry.captureException(errorObj);
  });
};

/**
 * HTTP status codes that represent expected failure modes (not bugs):
 *   401 — unauthenticated / token expired
 *   403 — forbidden (insufficient permission)
 *   404 — record not found (stale link, deleted by other session, etc.)
 *   409 — conflict (optimistic-lock collision, duplicate)
 *   410 — gone (deleted)
 *   422 — validation error (server told the user what to fix)
 *
 * These get captured as `breadcrumbs + info-level message` so they're
 * still visible in event timelines and aggregate Discover queries, but
 * they don't blow up the "Issues" view as error-level noise.
 *
 * Real bugs (500s, network failures, non-Axios exceptions) still go
 * through `captureException`.
 *
 * Rationale: a record-detail endpoint 404'ing because the user clicked
 * a stale link is not a bug worth waking anyone up at 3am. Sentry-3S
 * (LeadDetailDrawer / Lead not found) put 256+ events on a 15h window
 * because every user-clicks-deleted-lead hit was captured as error.
 */
const EXPECTED_HTTP_STATUSES = new Set([401, 403, 404, 409, 410, 422]);

/**
 * 403 is "expected" in the sense that it isn't a crash, but it is NEVER
 * routine: it means the backend denied something the UI offered the user.
 * Dropping 403s entirely is how the 2026-05→07 member self-pay outage
 * stayed invisible for two months (every member "Make a Payment" 403'd;
 * the events were captured, then filtered). So 403s get a dedicated
 * warning-level event, fingerprinted by method + normalized URL path so
 * each denied endpoint groups into ONE Sentry issue whose event count is
 * the spike detector — visible without flooding Issues.
 */
const normalizeUrlPath = (url: string): string =>
  url
    .split("?")[0]
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":id")
    .replace(/\/\d+(?=\/|$)/g, "/:id");

const capture403Denied = (error: unknown, endpoint?: string): void => {
  const axiosError = error instanceof AxiosError ? error : undefined;
  const method = axiosError?.config?.method?.toUpperCase() || "UNKNOWN";
  const rawUrl = axiosError?.config?.url || endpoint || "unknown";
  const path = normalizeUrlPath(rawUrl);
  const backendError =
    (axiosError?.response?.data as { error?: string } | undefined)?.error;

  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("error_type", "permission_denied");
    scope.setTag("http_status", "403");
    scope.setTag("http_method", method);
    scope.setTag("endpoint", path);
    scope.setExtras({
      url: rawUrl,
      method,
      backendError,
      message: toError(error).message,
    });
    scope.setFingerprint(["permission_denied", method, path]);
    Sentry.captureMessage(
      `permission_denied: ${method} ${path}${backendError ? ` (${backendError})` : ""}`
    );
  });
};

/**
 * Best-effort status extraction. Components in this codebase routinely
 * unwrap `AxiosError` into `new Error(err.response?.data?.message)`,
 * losing the original status. We try multiple shapes:
 *  - native AxiosError → response.status
 *  - wrapped Error with .status / .statusCode (some helpers attach it)
 *  - wrapped Error whose message exactly matches a known 404 phrase
 */
const extractHttpStatus = (error: unknown): number | undefined => {
  if (error instanceof AxiosError) return error.response?.status;
  if (error && typeof error === "object") {
    const e = error as Record<string, unknown>;
    if (typeof e.status === "number") return e.status;
    if (typeof e.statusCode === "number") return e.statusCode;
    if (e.response && typeof e.response === "object") {
      const r = e.response as Record<string, unknown>;
      if (typeof r.status === "number") return r.status;
    }
  }
  return undefined;
};

/**
 * Drop-in replacement for @sentry/react's captureException, used by the
 * api/ layer's catch blocks. Those catches historically imported the raw
 * SDK function, bypassing the EXPECTED_HTTP_STATUSES policy entirely —
 * so a staff account loading the member dashboard (GET /memberships →
 * 404) or a stale customer bookmark opened error-level Issues that
 * captureApiError would have treated as expected. AxiosErrors with an
 * expected status become breadcrumbs; everything else passes through to
 * the SDK unchanged.
 */
export const captureException = (
  error: unknown,
  hint?: Parameters<typeof Sentry.captureException>[1]
): string | undefined => {
  const status = extractHttpStatus(error);
  if (status === 403) {
    // Permission denials get their own grouped warning event — see
    // capture403Denied. Breadcrumb-only hid the member self-pay outage.
    capture403Denied(error);
    return undefined;
  }
  if (status !== undefined && EXPECTED_HTTP_STATUSES.has(status)) {
    Sentry.addBreadcrumb({
      category: "api",
      message: `Expected ${status}`,
      level: "info",
      data: { status, message: toError(error).message },
    });
    return undefined;
  }
  // Don't forward an explicit undefined hint — callers (and their tests)
  // expect the single-argument SDK call shape.
  return hint === undefined
    ? Sentry.captureException(error)
    : Sentry.captureException(error, hint);
};

/**
 * Capture an API error with request/response details
 */
export const captureApiError = (
  error: AxiosError | Error | unknown,
  endpoint: string,
  context?: SentryContext
): void => {
  const status = extractHttpStatus(error);
  const isExpected = status !== undefined && EXPECTED_HTTP_STATUSES.has(status);

  // 403 — a denial of something the UI offered is always signal-worthy;
  // grouped warning event instead of the breadcrumb-only expected path.
  if (status === 403) {
    capture403Denied(error, endpoint);
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag("error_type", "api_error");
    scope.setTag("endpoint", endpoint);
    scope.setLevel(isExpected ? "info" : "error");

    if (status !== undefined) scope.setTag("http_status", status.toString());

    if (error instanceof AxiosError) {
      scope.setTag("http_method", error.config?.method?.toUpperCase() || "unknown");

      scope.setExtras({
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        ...context,
      });
    } else if (context) {
      scope.setExtras(context);
    }

    const errorObj = toError(error);

    // Expected statuses are breadcrumb-ONLY. The previous captureMessage
    // here still opened a Sentry Issue per endpoint ("Expected 409:
    // /public/leads" accumulated 20 events) — defeating the whole point.
    // The breadcrumb keeps the timeline visible on any real error that
    // follows; expected outcomes are not errors and send no event.
    if (isExpected) {
      Sentry.addBreadcrumb({
        category: "api",
        message: `Expected ${status} on ${endpoint}`,
        level: "info",
        data: { endpoint, status, message: errorObj.message },
      });
    } else {
      Sentry.captureException(errorObj);
    }
  });
};

/**
 * Capture a payment-related error with additional payment context
 */
export const capturePaymentError = (
  error: Error | unknown,
  paymentContext: {
    stripeCustomerId?: string;
    paymentIntentId?: string;
    subscriptionId?: string;
    amount?: number;
    currency?: string;
    operation: string;
  }
): void => {
  Sentry.withScope((scope) => {
    scope.setLevel("error");
    scope.setTag("error_type", "payment_error");
    scope.setTag("payment_operation", paymentContext.operation);

    // Don't include full customer/payment IDs in tags (security)
    // But include them in extras for debugging
    scope.setExtras({
      ...paymentContext,
      stripeCustomerId: paymentContext.stripeCustomerId ? `***${paymentContext.stripeCustomerId.slice(-4)}` : undefined,
    });

    const errorObj = toError(error);
    Sentry.captureException(errorObj);
  });
};

/**
 * Capture an authentication error
 */
export const captureAuthError = (
  error: Error | unknown,
  operation: string,
  userId?: string
): void => {
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("error_type", "auth_error");
    scope.setTag("auth_operation", operation);

    if (userId) {
      scope.setTag("user_id", userId);
    }

    const errorObj = toError(error);
    Sentry.captureException(errorObj);
  });
};

/**
 * Capture a kiosk-related error
 */
export const captureKioskError = (
  error: Error | unknown,
  operation: string,
  context?: {
    kioskId?: string;
    athleteId?: string;
    programId?: string;
    query?: string;
    [key: string]: string | undefined;
  }
): void => {
  Sentry.withScope((scope) => {
    scope.setLevel("error");
    scope.setTag("error_type", "kiosk_error");
    scope.setTag("kiosk_operation", operation);

    if (context) {
      scope.setExtras(context);
    }

    const errorObj = toError(error);
    Sentry.captureException(errorObj);
  });
};

/**
 * Add a breadcrumb for tracking user actions
 */
export const addBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = "info"
): void => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level,
  });
};

/**
 * Set user context for all subsequent errors
 */
export const setUserContext = (user: {
  id: string;
  email?: string;
  role?: string;
  tenantId?: string;
}): void => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });

  if (user.role) {
    Sentry.setTag("user_role", user.role);
  }
  if (user.tenantId) {
    Sentry.setTag("tenant_id", user.tenantId);
  }
};

/**
 * Clear user context on logout
 */
export const clearUserContext = (): void => {
  Sentry.setUser(null);
};

/**
 * Tag the current Sentry scope with tenant branding so multi-tenant
 * error triage can filter by club. Called from the settings-load
 * paths so even unauthenticated browsing (the public landing page)
 * still surfaces "which brand was the user on when this fired."
 *
 * tenant_slug already lands via the API breadcrumb in axiosInstance,
 * but business_name is human-readable and prefereable in the Sentry
 * issue list ("Gymnastics of York" reads faster than the slug).
 */
export const setTenantBranding = (settings: {
  business_name?: string;
  primary_color?: string;
} | null | undefined): void => {
  if (!settings) return;
  if (settings.business_name) {
    Sentry.setTag("tenant_brand", settings.business_name);
  }
  if (settings.primary_color) {
    Sentry.setTag("tenant_primary_color", settings.primary_color);
  }
};

/**
 * Where Sentry may attach its `sentry-trace` / `baggage` headers.
 *
 * Derived from the API base URL the app actually calls (`api/config` BASE_URL),
 * so distributed tracing reaches the real backend in every deploy context. The
 * previous literal (`yourserver.io`, the Sentry quick-start placeholder) matched
 * nothing, so no request was ever traced end to end.
 *
 * The pattern pins the API's origin AND its path prefix, so the headers go only
 * to our own API, never to Stripe, Supabase or any other third party the
 * browser talks to (an unexpected header there can fail a CORS preflight).
 * A relative or malformed base URL adds nothing: Sentry already traces
 * same-origin requests by default.
 */
export const buildTracePropagationTargets = (
  apiBaseUrl: string | undefined
): Array<string | RegExp> => {
  const targets: Array<string | RegExp> = ["localhost"];
  if (!apiBaseUrl) return targets;
  let parsed: URL;
  try {
    parsed = new URL(apiBaseUrl);
  } catch {
    return targets;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return targets;
  const prefix = `${parsed.origin}${parsed.pathname.replace(/\/+$/, "")}`;
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  targets.push(new RegExp(`^${escaped}(?:[/?#]|$)`));
  return targets;
};

export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  // REACT_APP_NODE_ENV belongs in this chain. netlify.toml already sets it per
  // deploy context ("production" for [context.production], "staging" for
  // [context.develop] and [context.branch-deploy]) — but nothing read it here,
  // and REACT_APP_SENTRY_ENVIRONMENT is only ever set in local .env files, so
  // on Netlify this fell through to NODE_ENV. CRA bakes NODE_ENV="production"
  // into ANY `npm run build`, so the staging bundle reported
  // `environment: production` and staging errors were indistinguishable from
  // real production incidents in Sentry. The config was already correct; the
  // code was reading the wrong variable.
  const environment =
    process.env.REACT_APP_SENTRY_ENVIRONMENT ||
    process.env.REACT_APP_NODE_ENV ||
    process.env.NODE_ENV ||
    "development";

  // Only initialize Sentry if DSN is provided
  if (!dsn) {
    console.warn("Sentry DSN not configured. Error tracking is disabled.");
    return;
  }

  // Initialized through @mediatechgroup/observability's browser policy
  // (PM de2e4507): an automated browser and the expected failures (a fetch
  // with no response, a canceled request, the Outlook link scanner, a script
  // the bundle never shipped) are dropped first, then Club Central's own
  // beforeSend runs, then every kept event is redacted (credentials, emails,
  // the user cut to its id). Every other option passes through as written.
  // src/utils/__tests__/sentryPolicy.test.ts pins the keep and drop decisions
  // against the frozen hook. The root entry is imported, not /browser: CRA's
  // TypeScript resolves no subpath exports, and the root is runtime-neutral.
  Sentry.init(browserSentryOptions({
    dsn,
    environment,

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,

    // Distributed tracing goes to our own API only (see buildTracePropagationTargets).
    tracePropagationTargets: buildTracePropagationTargets(apiConfig.BASE_URL),

    // Capture Replay for 10% of all sessions,
    // plus 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // The keep, drop and demote decisions live in utils/sentryBeforeSend so
    // they can be tested and frozen on their own.
    beforeSend: makeBeforeSend(environment),
  }));
};
